/**
 * TAP access-check API. Thin wiring of @repo/access to TAP's stores and data
 * layer. TAP users have no role docs, no role-derived actions — their content
 * actions come entirely from the subscription they redeemed a code for.
 */
import { createAccessApi } from '@repo/access';
import {
    actionRequire,
    actions,
    contentActions,
    roleRequire,
    roles,
} from '@repo/config/Global';
import { isSubscriptionExpired } from '@repo/config/Licence';
import {
    userContentActions as userContentActionsStore,
    userSubscriptions as userSubscriptionsStore,
} from '@repo/stores/UserStore';
import { currentUser } from './stores.js';
import {
    COLLECTION_TYPES,
    getEntities,
    getEntitiesWhere,
    getUserSubscriptions,
} from './DB.js';

const access = createAccessApi({
    currentUser,
    userSubscriptionsStore,
    userContentActionsStore,
    getEntities,
    getEntitiesWhere,
    getUserSubscriptions: () => getUserSubscriptions({ uid: currentUserUid() }),
    isSubscriptionExpired,
    enums: { roles, actions, contentActions, actionRequire, roleRequire },
    collectionTypes: COLLECTION_TYPES,
});

function currentUserUid() {
    let uid = null;
    const unsub = currentUser.subscribe(v => { uid = v?.uid || null; });
    unsub();
    return uid;
}

export const hasRole = access.hasRole;
export const hasAction = access.hasAction;
export const hasContentAction = access.hasContentAction;
export const canAccessResource = access.canAccessResource;
export const rebuildSubscriptions = access.rebuildSubscriptions;
// Display-only projection of expired/revoked subscriptions (see factory.js).
// Never feed this into an access check — union it with `userContentActions`
// only where a lapsed grant needs to be *shown*.
export const expiredContentActions = access.expiredContentActions;
export const resetAccess = access.reset;
export const peekAccess = access.peek;
// TAP users have no role docs — mark the gate as ready once hydrateUser has
// rebuilt their subscription-derived content actions. Exposed for Auth.js.
export const markRolesReady = access.markRolesReady;
export const rolesReady = access.rolesReady;

// TAP users don't have any Education-side role, but their subscription's
// `intendedEndUserRole` is TEACHER — the product treats them as teachers for
// access-gating purposes. Every "teacher-only content" check should return
// true for a signed-in TAP user.
export function canAccessTeacherOnlyMaterial() {
    return true;
}

// Synchronous book-level access check used by the shared book accordion to
// enable rows without a per-resource callable round-trip. A TAP user's
// accessible books are the material ids on their subscription content-actions.
export function hasBookAccess(bookId) {
    if (!bookId) return false;
    let entries = [];
    const unsub = userContentActionsStore.subscribe((v) => {
        entries = v || [];
    });
    unsub();
    return entries.some(
        (entry) =>
            entry?.id !== 'role' && (entry?.materials || []).includes(bookId)
    );
}
